/**
 * @fileoverview WebRTCStreamer class
 *
 * @example
 * // Usage:
 * import { WebRTCStreamer } from './WebRTCStreamer'
 *
 * function Component() {
 *   // ...
 * }
 */

import axios from 'axios'
import { EventEmitter } from 'eventemitter3'
import i18n from 'i18next'
import config from './config'
import { Deferred } from './Deferred'
import { isMobile } from './isMobile'

/**
 * @function getLanguage
 * @description Get the language code
 * @param {string} lang - The language code
 * @returns {string} The language code
 */
function getLanguage(lang: string): string {
    if (lang === 'en_GB') return 'en-GB'
    if (lang === 'en_JDR') return 'en-GB'

    if (lang === 'en_CA') return 'en-CA'
    if (lang === 'en-CA') return 'en-CA'

    if (lang === 'fr_CA') return 'fr-CA'
    if (lang === 'fr-CA') return 'fr-CA'

    if (lang === 'fr_FR') return 'fr-FR'
    if (lang === 'fr-FR') return 'fr-FR'

    if (lang === 'ja_JP') return 'ja-JP'
    if (lang === 'ja-JP') return 'ja-JP'

    switch (lang?.slice(0, 2)) {
        case 'es':
            return 'es-US'
        default:
            return 'en-US'
    }
}

/**
 * @class WebRTCStreamer
 * @description Class for streaming audio to the backend
 */
export class WebRTCStreamer {
    /**
     * RCTPeerConnection instance
     */
    #conn: RTCPeerConnection

    /**
     * RTCDataChannel to transmit events in real-time
     */
    #dc: RTCDataChannel

    /**
     * Session description that was issued to us by the backend
     */
    #sdp: RTCSessionDescriptionInit

    /**
     * Recording identification
     */
    #recordingUUID: Promise<string>

    /**
     * Deferred object that resolves with a session UUID
     */
    #session: Deferred<string> = new Deferred()

    /**
     * Deferred object to indicate if the steramer is in the connected state
     */
    #isConnected: Deferred<boolean> = new Deferred()

    /**
     * Language to be used for the session
     */
    #lang = getLanguage(i18n.language)

    /**
     * Event Emitter to communicate back the "track" event
     */
    ee = new EventEmitter()

    /**
     * Constructor for the WebRTCStreamer class
     * @param {MediaStream} stream - The media stream to stream
     * @param {RTCConfiguration} conf - The RTC configuration
     */
    constructor(stream: MediaStream, conf: RTCConfiguration) {
        this.#connect(stream, conf)
    }

    #onTrack = (ev: RTCTrackEvent) => {
        const [remoteStream] = ev.streams
        this.ee.emit('track', remoteStream)
    }

    #onMessage = (ev: MessageEvent<string>) => {
        switch (ev.data) {
            case 'play.start':
                this.ee.emit('track.start')
                break
            case 'play.end':
                this.ee.emit('track.end')
                this.#recordingUUID = undefined
                break
        }
    }

    #onIceCandidate = async (e: RTCPeerConnectionIceEvent) => {
        if (e.candidate === null) {
            const localDescription = JSON.stringify({
                description: this.#conn.localDescription.toJSON(),
                language: this.#lang,
            })

            try {
                const { data } = await axios.post(config.webRTC.urls.sessions, localDescription)
                this.#sdp = data.description

                const description = new RTCSessionDescription(this.#sdp)
                await this.#conn.setRemoteDescription(description)
                this.#session.resolve(data.session)
            } catch (e) {
                console.error(e)
                this.#session.reject(e)
            }
        }
    }

    #onDataChannelOpen = () => {
        this.#isConnected.resolve(true)
        this.ee.emit('connected')
        console.timeEnd('connected')
    }

    /**
     * Establishes a WebRTC connection using the given media stream.
     * This connection is established in multiple steps:
     * 1. Form the RTCPeerConnection
     * 2. Add stream tracks to the connection. Connection without tracks won't be established
     * 3. Get the local descriptor and register it with the WebRTC server to get the session id
     *
     * @param {MediaStream} stream
     */
    async #connect(stream: MediaStream, rtcConfig: RTCConfiguration) {
        console.time('connected')
        this.#conn = new RTCPeerConnection(rtcConfig)
        this.#dc = this.#conn.createDataChannel('data')

        this.#conn.getSenders().forEach((sender) => {
            sender.getParameters().encodings.forEach((encoding) => {
                delete encoding.maxBitrate
            })
        })

        if (isMobile) {
            this.#conn.addTransceiver('video', {
                direction: 'recvonly',
            })

            this.#conn.addTransceiver('audio', {
                direction: 'sendrecv',
            })
        }

        this.#dc.addEventListener('open', this.#onDataChannelOpen)
        this.#dc.addEventListener('message', this.#onMessage)

        stream.getTracks().forEach((track: MediaStreamTrack) => {
            this.#conn.addTrack(track, stream)
        })

        this.#conn.addEventListener('track', this.#onTrack)
        this.#conn.addEventListener('icecandidate', this.#onIceCandidate)

        const desc: RTCSessionDescriptionInit = await this.#conn.createOffer({
            iceRestart: true,
        })
        // const sdp = desc.sdp?.replace('useinbandfec=1', 'useinbandfec=1; maxaveragebitrate=510000')
        await this.#conn.setLocalDescription(desc)
    }

    /**
     * Starts the streaming process
     * @returns {Promise<string>} The recording UUID
     */
    async start(): Promise<string> {
        await this.#isConnected.promise
        if (this.#recordingUUID) {
            return Promise.reject('Streaming in progress')
        }

        try {
            console.time('create a recording')
            const session = await this.#session.promise
            console.log('#session resolved')

            const { data } = await axios.post(config.webRTC.urls.recordings, {
                language: this.#lang,
                session,
            })
            console.timeEnd('create a recording')
            console.log(
                'recording link: ',
                `${config.webRTC.urls.recordings}/${data.recording}/media`
            )

            this.#recordingUUID = data.recording
        } catch (e) {
            console.error('Failed to establish a new session and start recording:', e)
        }

        return this.#recordingUUID
    }

    /**
     * Skips the current audio
     */
    async skip(): Promise<void> {
        await this.#isConnected.promise

        this.#dc.send('play.skip')
    }

    /**
     * Stops the streaming process
     */
    async stop(): Promise<void> {
        await this.#isConnected.promise

        const recordingUUID = await this.#recordingUUID
        await axios.delete(config.webRTC.urls.recordings + '/' + recordingUUID)

        this.#conn.removeEventListener('icecandidate', this.#onIceCandidate)
        this.#conn.removeEventListener('track', this.#onTrack)

        this.#dc.removeEventListener('open', this.#onDataChannelOpen)
        this.#dc.removeEventListener('message', this.#onMessage)

        if (isMobile) {
            this.#conn.getTransceivers().map((transceiver) => transceiver.stop())
        }

        this.#conn.close()

        this.#conn.getSenders().forEach(({ track }) => {
            if (track?.readyState == 'live') {
                track.stop()
            }
        })
        this.#conn = undefined
        this.#sdp = undefined
    }

    /**
     * Plays the given audio
     * @param {string} audio - The audio to play
     */
    async play(audio: string) {
        await this.#isConnected.promise
        console.time('playing an audio: ' + audio)
        const session = await this.#session.promise
        await axios.post(`${config.webRTC.urls.sessions}/${session}/play`, {
            file: audio,
        })
        console.timeEnd('playing an audio: ' + audio)
    }
}
